import { Link, Outlet } from 'react-router-dom';
import { useState, useEffect } from 'react';

const competitions = [
  { name: 'Pakistan Mathematical Modeling Challenge', path: '/competitions/pmmc' },
  { name: 'World Mathematics Team Championship', path: '/competitions/wmtc' },
  { name: 'Pakistan Geography Olympiad', path: '/competitions/pgo' },
];

const SCROLL_THRESHOLD = 60;

export default function Layout() {
  const [navOpen, setNavOpen] = useState(false);
  const [competitionsOpen, setCompetitionsOpen] = useState(false);
  const [navFloating, setNavFloating] = useState(false);

  useEffect(() => {
    const handleScroll = () => setNavFloating(window.scrollY > SCROLL_THRESHOLD);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeNav = () => {
    setNavOpen(false);
    setCompetitionsOpen(false);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 text-gray-900 relative overflow-hidden">
      {/* Animated Background Shapes */}
      <div className="fixed inset-0 pointer-events-none z-0">
        {/* Floating Circle 1 */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full opacity-30 animate-pulse animate-float"></div>
        
        {/* Floating Circle 2 */}
        <div className="absolute top-40 right-20 w-24 h-24 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full opacity-25 animate-pulse animate-float-delay-2"></div>
        
        {/* Floating Triangle */}
        <div className="absolute bottom-40 left-1/4 w-20 h-20 bg-gradient-to-br from-blue-100 to-cyan-100 opacity-20 animate-pulse animate-float-delay-1" 
             style={{ clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }}></div>
        
        {/* Floating Square */}
        <div className="absolute top-1/3 right-1/3 w-16 h-16 bg-gradient-to-br from-purple-100 to-indigo-100 opacity-20 animate-pulse rotate-45 animate-float-delay-3"></div>
        
        {/* Floating Hexagon */}
        <div className="absolute bottom-20 right-10 w-28 h-28 bg-gradient-to-br from-cyan-100 to-blue-100 opacity-25 animate-pulse animate-float-delay-4" 
             style={{ clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }}></div>
        
        {/* Small floating dots */}
        <div className="absolute top-1/2 left-1/6 w-3 h-3 bg-blue-200 rounded-full opacity-40 animate-pulse animate-float-delay-05"></div>
        <div className="absolute top-1/3 right-1/6 w-2 h-2 bg-purple-200 rounded-full opacity-30 animate-pulse animate-float-delay-15"></div>
        <div className="absolute bottom-1/3 left-1/2 w-4 h-4 bg-cyan-200 rounded-full opacity-35 animate-pulse animate-float-delay-25"></div>
      </div>

      {/* Top Information Bar - hidden on mobile */}
      <div className="hidden md:block w-full bg-black text-white text-xs sm:text-sm py-2 relative z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-start gap-1 sm:gap-2">
          <span className="font-medium">
            Please contact us for assistance.
          </span>
          <a
            href="mailto:admin@capstonepakistan.org"
            className="underline underline-offset-2 hover:text-gray-200 transition-colors"
          >
            admin@capstonepakistan.org
          </a>
        </div>
      </div>

      {/* Spacer when navbar is floating so layout doesn't jump */}
      {navFloating && <div className="h-[4.5rem] shrink-0" aria-hidden="true" />}

      {/* Main Navbar - part of hero when at top, floats when scrolled */}
      <nav
        className={`bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 z-50 backdrop-blur-sm transition-all duration-300 ${
          navFloating
            ? 'fixed top-4 left-0 right-0 mx-4 rounded-2xl shadow-2xl'
            : 'relative w-full rounded-none shadow-none'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <Link to="/" className="flex flex-col items-start md:items-center space-y-0 md:space-y-0">
              <span className="text-2xl font-extrabold tracking-tight text-white leading-none">CAPSTONE</span>
              <span className="text-xs font-semibold text-white/80 tracking-widest">PAKISTAN</span>
            </Link>
            
            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              <Link to="/" className="text-white/90 hover:text-white font-medium transition-colors">Home</Link>
              <Link to="/about" className="text-white/90 hover:text-white font-medium transition-colors">About</Link>
              <Link to="/blogs" className="text-white/90 hover:text-white font-medium transition-colors">Resources</Link>
              
              {/* Programs Dropdown */}
              <div className="relative group">
                <button className="flex items-center space-x-1 text-white/90 hover:text-white font-medium transition-colors">
                  <span>Programs</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div className="absolute top-full left-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Academic Programs</h3>
                    {competitions.map((comp) => (
                      <Link 
                        key={comp.path}
                        to={comp.path}
                        className="block py-2 px-3 rounded hover:bg-gray-50 text-gray-700 hover:text-blue-700 transition-colors"
                      >
                        {comp.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
              
              <a href="mailto:admin@capstonepakistan.org" className="bg-white/20 hover:bg-white/30 text-white px-6 py-2 rounded-lg font-medium transition-colors border border-white/30">
                Contact Us
              </a>
            </div>

            {/* Mobile menu button */}
            <button 
              className="lg:hidden p-2 focus:outline-none text-white" 
              onClick={() => setNavOpen(!navOpen)}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
          
          {/* Mobile Navigation */}
          {navOpen && (
            <div className="lg:hidden py-4 border-t border-white/20">
              <div className="space-y-4">
                <Link to="/" className="block text-white/90 hover:text-white font-medium" onClick={closeNav}>Home</Link>
                <Link to="/about" className="block text-white/90 hover:text-white font-medium" onClick={closeNav}>About</Link>
                <Link to="/blogs" className="block text-white/90 hover:text-white font-medium" onClick={closeNav}>Resources</Link>
                
                <div>
                  <button 
                    className="flex items-center justify-between w-full py-2 text-white/90 hover:text-white font-medium"
                    onClick={() => setCompetitionsOpen(!competitionsOpen)}
                  >
                    <span>Programs</span>
                    <svg className={`w-4 h-4 transition-transform ${competitionsOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {competitionsOpen && (
                    <div className="ml-4 mt-2 space-y-2">
                      {competitions.map((comp) => (
                        <Link 
                          key={comp.path}
                          to={comp.path}
                          className="block py-2 text-white/70 hover:text-white"
                          onClick={closeNav}
                        >
                          {comp.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
                
                <a href="mailto:admin@capstonepakistan.org" className="block bg-white/20 hover:bg-white/30 text-white px-6 py-2 rounded-lg font-medium text-center border border-white/30">
                  Contact Us
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>

      <main className="flex-1 relative z-10">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-black text-white relative z-10">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Brand Section */}
            <div className="lg:col-span-1">
              <div className="flex flex-col items-start space-y-0">
                <span className="text-xl font-extrabold tracking-tight text-blue-200 leading-none">CAPSTONE</span>
                <span className="text-xs font-semibold text-gray-400 tracking-widest">PAKISTAN</span>
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Empowering Pakistan's brightest minds through nationwide assessments
              </p>

            </div>
            
            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-semibold mb-6 text-white">Quick Links</h3>
              <ul className="space-y-3">
                <li><Link to="/" className="text-gray-300 hover:text-white transition-colors">Home</Link></li>
                <li><Link to="/about" className="text-gray-300 hover:text-white transition-colors">About Us</Link></li>
                <li><Link to="/blogs" className="text-gray-300 hover:text-white transition-colors">Resources</Link></li>
                <li><a href="mailto:admin@capstonepakistan.org" className="text-gray-300 hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            
            {/* Programs */}
            <div>
              <h3 className="text-lg font-semibold mb-6 text-white">Academic Programs</h3>
              <ul className="space-y-3">
                <li><Link to="/competitions/pmmc" className="text-gray-300 hover:text-white transition-colors">Pakistan Mathematical Modeling Challenge</Link></li>
                <li><Link to="/competitions/wmtc" className="text-gray-300 hover:text-white transition-colors">World Mathematics Team Championship</Link></li>
                <li><Link to="/competitions/pgo" className="text-gray-300 hover:text-white transition-colors">Pakistan Geography Olympiad</Link></li>
              </ul>
            </div>
            
            {/* Contact Info */}
            <div>
              <h3 className="text-lg font-semibold mb-6 text-white">Contact Information</h3>
              <div className="space-y-3 text-gray-300">
                <a href="mailto:admin@capstonepakistan.org" className="flex items-center hover:text-white transition-colors">
                  <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                  </svg>
                  admin@capstonepakistan.org
                </a>
              </div>
              <div className="mt-4 rounded-lg overflow-hidden border border-gray-700">
                <a
                  href="https://www.google.com/maps/search/?api=1&query=sf+unit+113+Hub+river+side+road+SITE+Karachi"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="View on Google Maps"
                >
                  <iframe
                    title="Map showing Capstone Pakistan office location"
                    src="https://www.openstreetmap.org/export/embed.html?bbox=67.02%2C24.86%2C67.06%2C24.90&layer=mapnik&marker=24.88%2C67.04"
                    className="w-full h-40 border-0 block"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </a>
              </div>
            </div>
          </div>
          
          {/* Bottom Footer */}
          <div className="border-t border-gray-800 pt-8 mt-8 flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-400 text-sm mb-4 md:mb-0">
              © {new Date().getFullYear()} Capstone Pakistan. All rights reserved.
            </div>
            <div className="flex space-x-6 text-sm text-gray-400">
              <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms-of-service" className="hover:text-white transition-colors">Terms of Service</Link>
              <Link to="/cookie-policy" className="hover:text-white transition-colors">Cookie Policy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
} 